import {useEffect, useRef, useState} from 'react';
import {useInView} from 'react-intersection-observer';
import {useIsMounted} from '@/hooks/useIsMounted';

// Delay in milliseconds between checks whether the next page can be fetched
const NEXT_PAGE_DELAY = 100;

type Options = {
  enabled?: boolean;
  fetching?: boolean;
  offset?: string;
  selector?: string;
  onNextPage?(): void;
}

export function useInfiniteScroll<T extends HTMLElement>({
  enabled = true,
  fetching,
  offset = '200px',
  selector,
  onNextPage,
}: Options) {
  const isFetchingRef = useRef(fetching);
  const inViewRef = useRef(false);
  const [isRendered, setIsRendered] = useState(false);

  const listElementRef = useRef<T | null>(null);
  const [listRows, setListRows] = useState(0);

  const isMounted = useIsMounted();
  const { ref, inView } = useInView({
    rootMargin: offset,
    skip: !isMounted,
    onChange(inView) {
      inViewRef.current = inView;
    },
  });

  const listElement = (element: T | null) => {
    listElementRef.current = element;
    if (element) {
      setIsRendered(true);
    }
  };

  useEffect(() => {
    if (isRendered && enabled && onNextPage) {
      // Disconnecting the observer does not cancel checks it has already scheduled, so they are
      // tracked and cleared on cleanup. Otherwise a resize just before unmount lands setListRows
      // after teardown.
      const pendingChecks = new Set<ReturnType<typeof setTimeout>>();

      const observer = new ResizeObserver(([entry]) => {
        const check = setTimeout(() => {
          pendingChecks.delete(check);
          setListRows(selector
            ? entry.target.querySelectorAll(selector).length
            : entry.target.childElementCount);
        }, NEXT_PAGE_DELAY);

        pendingChecks.add(check);
      });

      if (listElementRef.current) {
        observer.observe(listElementRef.current);
      }

      return () => {
        pendingChecks.forEach(clearTimeout);
        pendingChecks.clear();
        observer.disconnect();
      };
    }
  }, [isRendered, enabled, onNextPage]);

  useEffect(() => {
    isFetchingRef.current = fetching;
  }, [fetching]);

  useEffect(() => {
    if (!isFetchingRef.current && inViewRef.current && listRows) {
      onNextPage?.();
    }
  }, [inView, listRows]);

  return {
    ref,
    listElement,
  };
}
